import { useMemo } from 'react'
import { useFonts } from '@/providers/font'

/** The book/checkpoint default font family: its id and the name the picker's pinned row shows. */
export function useDefaultFontFamily(
  defaultFontFamilyId: number | undefined,
  defaultFontName: string | undefined
) {
  const { getFamilyById, getFamilyIdByCssName } = useFonts()

  const resolvedDefaultFontFamilyId = useMemo(() => {
    // Book-settings id is authoritative; fall back to checkpoint name lookup when id is absent.
    if (defaultFontFamilyId != null) return defaultFontFamilyId

    if (defaultFontName) {
      // Normalizes (trim/collapse-whitespace/lowercase) and checks both `displayName` and
      // `familyName` — an exact-match `.find` silently failed whenever the checkpoint's NSD
      // `DefaultFont` string differed from the API's `familyName` spelling/casing.
      const byName = getFamilyIdByCssName(defaultFontName)

      if (byName != null) return byName
    }

    return undefined
  }, [defaultFontFamilyId, defaultFontName, getFamilyIdByCssName])

  const defaultFontFamilyName = useMemo(() => {
    if (!resolvedDefaultFontFamilyId) return defaultFontName ?? null
    const family = getFamilyById(resolvedDefaultFontFamilyId)

    return family?.displayName ?? defaultFontName ?? null
  }, [resolvedDefaultFontFamilyId, getFamilyById, defaultFontName])

  return { resolvedDefaultFontFamilyId, defaultFontFamilyName }
}
